import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { Layers, Building2, Activity, Clock, History, FileCheck, MapPin, Wrench, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AddFilterMenu, type FilterCategory } from "@/components/ui/add-filter-menu";
import { ChipFilter } from "@/components/ui/chip-filter";
import {
  TRAILER_FILTER_LABELS,
  isFilterActive,
  type TrailerFilterKey,
  type TrailerFilterOption,
} from "@/lib/trailer-filters";

export const TRAILER_FILTER_ICONS: Record<TrailerFilterKey, ReactNode> = {
  type: <Layers className="h-3.5 w-3.5" />,
  scac: <Building2 className="h-3.5 w-3.5" />,
  status: <Activity className="h-3.5 w-3.5" />,
  dwell: <Clock className="h-3.5 w-3.5" />,
  event: <History className="h-3.5 w-3.5" />,
  docs: <FileCheck className="h-3.5 w-3.5" />,
  location: <MapPin className="h-3.5 w-3.5" />,
  service: <Wrench className="h-3.5 w-3.5" />,
};

interface TrailerFilterBarProps {
  /** Filter keys this page offers, in display order. */
  keys: readonly TrailerFilterKey[];
  values: Partial<Record<TrailerFilterKey, string[]>>;
  options: Partial<Record<TrailerFilterKey, TrailerFilterOption[]>>;
  onChange: (key: TrailerFilterKey, value: string[]) => void;
  onResetAll: () => void;
  /** Rendered before the filter chips (search chip, group-by control, …). */
  leadingSlot?: ReactNode;
  /** Rendered right-aligned (result count, search box, …). */
  trailingSlot?: ReactNode;
}

/**
 * Shared chip filter bar used by /trailers and /yard-view so both pages
 * expose identical filter behavior. Chips are derived from which filters
 * hold a non-default value, plus any the user opened manually.
 */
export function TrailerFilterBar({
  keys,
  values,
  options,
  onChange,
  onResetAll,
  leadingSlot,
  trailingSlot,
}: TrailerFilterBarProps) {
  const [openFilterKey, setOpenFilterKey] = useState<TrailerFilterKey | null>(null);
  const [manuallyAdded, setManuallyAdded] = useState<TrailerFilterKey[]>([]);
  const ignoreAutoCloseRef = useRef<TrailerFilterKey | null>(null);

  const activeFilters = useMemo(() => {
    const chips: TrailerFilterKey[] = [];
    for (const k of keys) if (isFilterActive(values[k])) chips.push(k);
    for (const k of manuallyAdded) if (keys.includes(k) && !chips.includes(k)) chips.push(k);
    return chips;
  }, [keys, values, manuallyAdded]);

  const addFilter = useCallback((key: string) => {
    const fk = key as TrailerFilterKey;
    setManuallyAdded((prev) => (prev.includes(fk) ? prev : [...prev, fk]));
    // Defer opening so the chip is mounted before the popover anchors to it.
    window.setTimeout(() => {
      ignoreAutoCloseRef.current = fk;
      setOpenFilterKey(fk);
      window.setTimeout(() => {
        if (ignoreAutoCloseRef.current === fk) ignoreAutoCloseRef.current = null;
      }, 250);
    }, 50);
  }, []);

  const handleOpenChange = useCallback((key: TrailerFilterKey, isOpen: boolean) => {
    if (!isOpen && ignoreAutoCloseRef.current === key) {
      ignoreAutoCloseRef.current = null;
      return; // suppress the spurious close right after mounting
    }
    setOpenFilterKey((prev) => (isOpen ? key : prev === key ? null : prev));
  }, []);

  const removeFilter = useCallback(
    (key: TrailerFilterKey) => {
      setManuallyAdded((prev) => prev.filter((k) => k !== key));
      setOpenFilterKey((prev) => (prev === key ? null : prev));
      if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
      onChange(key, ["all"]);
    },
    [onChange],
  );

  const handleResetAll = useCallback(() => {
    setManuallyAdded([]);
    setOpenFilterKey(null);
    ignoreAutoCloseRef.current = null;
    onResetAll();
  }, [onResetAll]);

  const availableCategories: FilterCategory[] = keys
    .filter((k) => !activeFilters.includes(k))
    .map((k) => ({
      key: k,
      label: TRAILER_FILTER_LABELS[k],
      icon: TRAILER_FILTER_ICONS[k] ?? <Filter className="h-3.5 w-3.5" />,
    }));

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {leadingSlot}

      {activeFilters.map((key) => (
        <ChipFilter
          key={key}
          label={TRAILER_FILTER_LABELS[key]}
          icon={TRAILER_FILTER_ICONS[key]}
          value={values[key] ?? ["all"]}
          onChange={(v) => onChange(key, Array.isArray(v) ? v : [v])}
          options={options[key] ?? []}
          onRemove={() => removeFilter(key)}
          multiSelect
          open={openFilterKey === key}
          onOpenChange={(o) => handleOpenChange(key, o)}
        />
      ))}

      <AddFilterMenu
        categories={availableCategories}
        onSelect={addFilter}
        iconOnly={activeFilters.length > 0}
      />

      {activeFilters.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 text-sm text-muted-foreground"
          onClick={handleResetAll}
        >
          Reset filters
        </Button>
      )}

      {trailingSlot && <div className="ml-auto flex items-center gap-3">{trailingSlot}</div>}
    </div>
  );
}
